import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import VendorSelectionModal from './components/VendorSelectionModal';
import VendorDetails from './components/VendorDetails';
import { BalanceInfo, Carrier, MappedVendor, Transaction, Vendor } from './constants';
import API from '@Services/apiAxios';
import moment from 'moment';
const CreditsTransactionPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<Carrier | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [carrierLoading, setCarrierLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<any>(null);
  const [balance, setBalance] = useState<BalanceInfo>({
    totalInvoicedPaidAmount: 0,
    totalInvoicedAmount: 0,
  });
  const [creditNoteBalance, setCreditNoteBalance] = useState<number>(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setCarrierLoading(true);
        const response = await API.get('/invoice/purchase-invoice/carriers');
        const data = response.data.data;
        setCarriers(data);
        setVendors(
          data.map(
            (vendor: Carrier): MappedVendor => ({
              id: vendor._id,
              name: vendor.name,
              totalOutstandingBalance: vendor.totalOutstandingBalance || 0,
              paidInvoices: vendor.paidInvoicesCount || 0,
              unpaidInvoices: vendor.unpaidInvoicesCount || 0,
              cnBalance: vendor.cnBalance || 0,
            }),
          ),
        );
      } catch (err) {
        console.error('Error fetching carriers:', err);
      } finally {
        setCarrierLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch transactions when vendor, filters, or pagination changes
  useEffect(() => {
    if (selectedVendor) {
      fetchTransactions(selectedVendor._id, currentFilters, page, rowsPerPage);
    }
  }, [selectedVendor, currentFilters, page, rowsPerPage]);

  useEffect(() => {
    if (!selectedVendor) return;

    const fetchBalance = async () => {
      setBalanceLoading(true);

      try {
        const [balanceRes, creditRes] = await Promise.all([
          API.get(`/invoice/purchase-invoice/balance/carrier/${selectedVendor?._id}`),
          API.get(`/payment/purchase-invoice/credit-note-balance/${selectedVendor?._id}`),
        ]);

        setBalance(balanceRes.data);
        setCreditNoteBalance(creditRes.data?.data?.balance || 0);
      } catch (err: any) {
        console.error('Error fetching balance data:', err);
      } finally {
        setBalanceLoading(false);
      }
    };

    fetchBalance();
  }, [selectedVendor]);

  const fetchTransactions = async (carrierId: string, filters: any = null, pageNum = 0, pageSize = 10) => {
    setLoading(true);
    try {
      // Default date range (last 1 month)
      const defaultFromDate = moment().subtract(1, 'months').format('DD/MM/YYYY');
      const defaultToDate = moment().format('DD/MM/YYYY');

      const params: any = {
        fromDate: filters?.fromDate || defaultFromDate,
        toDate: filters?.toDate || defaultToDate,
        carrierId,
        page: pageNum + 1, // API usually expects 1-based indexing
        limit: pageSize,
      };

      // Add status filter if provided
      if (filters?.status && filters.status.length > 0) {
        params.status = filters.status.join(',');
      }

      // Add mode filter if provided
      if (filters?.mode && filters.mode.length > 0) {
        params.mode = filters.mode.join(',');
      }

      // Add dynamic search filters (e.g., invoiceNumber: "BRIGHTLINKERS00007825")
      Object.keys(filters || {}).forEach(key => {
        // Exclude filters that are already handled above
        if (!['status', 'fromDate', 'toDate'].includes(key) && filters[key]) {
          params[key] = filters[key];
        }
      });

      const response = await API.get(`/payment/purchase-invoice/transaction/all`, {
        params,
      });

      if (response.data?.data) {
        // Handle different response structures
        if (response.data.data.list) {
          setTransactions(response.data.data.list);
          setTotalCount(response.data.data.totalCount || response.data.data.list.length);
        } else if (Array.isArray(response.data.data)) {
          setTransactions(response.data.data);
          setTotalCount(response.data.data.length);
        } else {
          setTransactions([]);
          setTotalCount(0);
        }
      } else {
        setTransactions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (filters: any) => {
    setCurrentFilters(filters);
    setPage(0); // Reset to first page when filters change
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleVendorSelect = async (vendor: Vendor) => {
    const vendorDetails = carriers.find(v => v._id === vendor.id);

    if (vendorDetails) {
      setSelectedVendor(vendorDetails);
      setIsModalOpen(false);
      // Reset filters and pagination when vendor changes
      setCurrentFilters(null);
      setPage(0);
      setRowsPerPage(10);
    }
  };

  const handleModalClose = () => {
    if (!selectedVendor) {
      navigate(-1);
    }
  };

  return (
    <Box>
      {/* Vendor Selection Modal */}
      <VendorSelectionModal
        open={isModalOpen}
        vendors={vendors}
        onVendorSelect={handleVendorSelect}
        onClose={handleModalClose}
        carrierLoading={carrierLoading}
      />

      {/* Vendor Details */}
      {selectedVendor && (
        <VendorDetails
          setIsModalOpen={setIsModalOpen}
          isModalOpen={isModalOpen}
          vendor={selectedVendor}
          vendors={vendors}
          onVendorSelect={handleVendorSelect}
          loading={loading}
          transactions={transactions}
          onFiltersChange={handleFiltersChange}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          balance={balance}
          creditNoteBalance={creditNoteBalance}
          balanceLoading={balanceLoading}
        />
      )}
    </Box>
  );
};

export default CreditsTransactionPage;
